// Renderer-lifetime custody only. Claims deliberately retain no request config,
// credentials, callbacks, server replies or instructions to replay a write.
const claims = new Map();
const observers = new Set();
let nextId = 0;
const emit = () => observers.forEach(notify => { try { notify(); } catch { /* Observation cannot change write custody. */ } });
const local = spec => spec.source !== 'cloud';
const scopeMatches = (owner, claim) => owner?.endpoint === claim.endpoint && owner?.tenant === claim.tenant;
const actorMatches = (owner, claim) => scopeMatches(owner, claim) && owner?.actor === claim.actor;
const resources = spec => [spec.kind === 'create' ? `create:${spec.date}` : `run:${spec.runId}`,
  ...(spec.orderId ? [`order:${spec.orderId}`] : [])];
const valid = spec => spec && ['cloud', 'draft', 'cached'].includes(spec.source)
  && typeof spec.kind === 'string' && !!spec.kind && typeof spec.date === 'string' && !!spec.date
  && (spec.kind === 'create' || (typeof spec.runId === 'string' && !!spec.runId));
const defaultRefusal = error => [401, 403].includes(error?.response?.status)
  ? 'Permission unavailable. Sign in and review current access.' : null;
const duplicateRisk = claim => ['create', 'notify'].includes(claim.kind);

export function createRunCommands(capture, read) {
  let active = false;
  let generation = 0;
  let snapshot = [];
  const listeners = new Set();
  const reviews = new Map();
  const ownerFor = spec => capture(local(spec));
  const current = owner => !!owner?.admin && !!owner.current?.();
  const visible = claim => {
    const owner = ownerFor(claim);
    return current(owner) && actorMatches(owner, claim);
  };
  const evidenceFor = claim => {
    const evidence = reviews.get(claim.id);
    return evidence?.generation === generation && evidence?.current?.() ? evidence : null;
  };
  const notify = () => {
    snapshot = active ? [...claims.values()].filter(visible).map(claim => {
      const evidence = evidenceFor(claim);
      return { id: claim.id, kind: claim.kind, source: claim.source, date: claim.date,
        runId: claim.runId, orderId: claim.orderId, name: claim.name, state: claim.state,
        stalled: claim.stalled, message: claim.message, refreshState: claim.refreshState, review: evidence?.message,
        canAcknowledge: claim.state === 'unknown' && evidence?.ready === true,
        duplicateRisk: duplicateRisk(claim) || evidence?.duplicateRisk === true };
    }) : [];
    listeners.forEach(listener => { try { listener(); } catch { /* Isolate presentation observers. */ } });
  };
  const find = id => {
    const claim = claims.get(id);
    return claim && visible(claim) ? claim : null;
  };
  const conflicts = (spec, owner) => [...claims.values()].some(claim => scopeMatches(owner, claim)
    && resources(spec).some(resource => resources(claim).includes(resource)));
  const blocked = spec => {
    if (!active || !valid(spec)) return true;
    const owner = ownerFor(spec);
    return !current(owner) || !owner.actor || conflicts(spec, owner);
  };
  return {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => snapshot,
    activate() { generation += 1; active = true; reviews.clear(); observers.add(notify); notify(); },
    retire() { generation += 1; active = false; reviews.clear(); observers.delete(notify); notify(); },
    blocked,
    blockedOrder(orderId, source) {
      if (!orderId) return false;
      if (!active || !['cloud', 'draft', 'cached'].includes(source)) return true;
      const owner = ownerFor({ source });
      return !current(owner) || [...claims.values()].some(claim => scopeMatches(owner, claim)
        && claim.orderId === orderId);
    },
    async run(spec, { send, accept, message = 'Change accepted.', current: callerCurrent = () => true,
      onAccepted, refusal = defaultRefusal } = {}) {
      if (blocked(spec) || !callerCurrent() || typeof send !== 'function' || typeof accept !== 'function') {
        return { state: 'blocked', accepted: false };
      }
      const owner = ownerFor(spec);
      const started = generation;
      const owns = () => active && generation === started && current(owner) && callerCurrent();
      if (!owns() || conflicts(spec, owner)) return { state: 'blocked', accepted: false };
      const claim = { id: `run-command-${++nextId}`, endpoint: owner.endpoint, tenant: owner.tenant,
        actor: owner.actor, kind: spec.kind, source: spec.source, date: spec.date,
        runId: spec.runId, orderId: spec.orderId,
        ...(spec.kind === 'create' ? { name: spec.name } : {}), state: 'pending', stalled: false,
        message: 'Waiting for the command result.' };
      claims.set(claim.id, claim); emit();
      // A synchronous observer can retire this screen before dispatch.
      if (!owns()) { claims.delete(claim.id); emit(); return { state: 'retired', accepted: false }; }
      const timer = setTimeout(() => {
        claim.stalled = true; claim.message = 'Still waiting. Do not submit this command again.'; emit();
      }, 30000);
      let data; let accepted = false; let refused;
      try {
        data = await send(owner.config);
        accepted = accept(data) === true;
      } catch (error) {
        try { refused = refusal(error); } catch { /* A failed classifier cannot prove refusal. */ }
      } finally { clearTimeout(timer); }
      claim.state = accepted ? 'accepted' : refused ? 'refused' : 'unknown';
      claim.message = accepted ? message : typeof refused === 'string' ? refused
        : 'Result not confirmed. Check current state before trying again.';
      // Acknowledgement precedes the automatic readback. It is not evidence
      // that readback failed, and presentation must remain quiet until it settles.
      if (accepted) claim.refreshState = owns() && onAccepted ? 'pending' : 'needed';
      if (refused && !accepted) claims.delete(claim.id);
      emit();
      if (accepted && owns() && onAccepted) {
        try {
          await onAccepted(data);
          claim.refreshState = owns() ? 'complete' : 'needed'; emit();
        }
        catch {
          // The write acknowledgement is independent of follow-up publication.
          claim.refreshState = 'failed';
          claim.message = 'Change accepted; refresh unavailable.'; emit();
        }
      } else if (accepted && claim.refreshState === 'pending') {
        // An observer may retire the owner when acknowledgement is published,
        // before its follow-up starts. A replacement screen must offer recovery.
        claim.refreshState = 'needed'; emit();
      }
      return { state: owns() ? claim.state : 'retired', accepted, id: claim.id,
        ...(owns() ? { message: claim.message, ...(accepted ? { data } : {}) } : {}) };
    },
    refreshed(id) {
      const claim = find(id);
      if (!active || !claim || claim.state !== 'accepted') return false;
      claims.delete(id); reviews.delete(id); emit(); return true;
    },
    async review(id, observe) {
      const claim = find(id);
      if (!active || !claim || claim.state !== 'unknown' || typeof observe !== 'function') return false;
      const owner = ownerFor(claim); const started = generation;
      const attempt = { generation: started, current: owner.current };
      reviews.set(id, attempt); notify();
      const owns = () => active && generation === started && current(owner) && actorMatches(owner, claim)
        && claims.get(id) === claim && reviews.get(id) === attempt;
      if (!owns()) return false;
      // Each dispatch through the bounded reader rechecks the original review.
      const guardedRead = (execute, config = owner.config) => read(requestConfig => {
        if (!owns()) throw new Error('Observation retired before dispatch');
        return execute(requestConfig);
      }, config);
      try {
        const result = await observe({ ...claim, endpoint: undefined, tenant: undefined, actor: undefined }, owner, guardedRead);
        if (!owns()) return false;
        reviews.set(id, { generation: started, current: owner.current, ready: result?.ready === true,
          message: typeof result?.message === 'string' ? result.message : 'Current state could not be verified.',
          duplicateRisk: duplicateRisk(claim) || result?.duplicateRisk === true });
      } catch {
        if (!owns()) return false;
        reviews.set(id, { generation: started, current: owner.current, ready: false,
          message: 'Current state could not be verified. No retry has been authorized.' });
      }
      notify(); return true;
    },
    acknowledge(id, { allowDuplicate = false } = {}) {
      const claim = find(id); const evidence = claim && evidenceFor(claim);
      if (!active || !claim || claim.state !== 'unknown' || !evidence?.ready
        || ((duplicateRisk(claim) || evidence.duplicateRisk) && !allowDuplicate)) return false;
      claims.delete(id); reviews.delete(id); emit(); return true;
    },
  };
}
